import { useEffect, useRef } from 'react';
import api from '../utils/axiosConfig';
import { maybeOpenStravaConnectModalFromSyncEvent } from '../utils/stravaReconnectModal';

/**
 * Global listener for stravaRequestIncrementalSync. Ensures the refresh button
 * in RecentActivities works even when StravaConnect widget/modal is not mounted.
 */
const StravaSyncListener = () => {
  const abortRef = useRef(null);

  useEffect(() => {
    const runIncrementalSync = async () => {
      window.dispatchEvent(new Event('stravaSyncStarted'));

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await api.get('/api/auth/profile');

        const response = await fetch('/api/strava/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            // Incremental sync only detail-fetches activities new in this run (server-side).
            includeDetails: false,
            overwrite: false,
            incremental: true,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Sync failed (${response.status})`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedComplete = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'complete') {
                receivedComplete = true;
                window.dispatchEvent(new Event('stravaSyncComplete'));
                window.dispatchEvent(new Event('trainingUpdated'));
                break;
              }
              if (event.type === 'error') {
                receivedComplete = true;
                maybeOpenStravaConnectModalFromSyncEvent(event);
                break;
              }
            } catch { /* skip */ }
          }
        }

        if (!receivedComplete) {
          window.dispatchEvent(new Event('stravaSyncComplete'));
          window.dispatchEvent(new Event('trainingUpdated'));
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Incremental sync failed:', err);
        }
      } finally {
        abortRef.current = null;
        window.dispatchEvent(new Event('stravaSyncEnded'));
      }
    };

    const handler = () => runIncrementalSync();
    window.addEventListener('stravaRequestIncrementalSync', handler);
    return () => {
      window.removeEventListener('stravaRequestIncrementalSync', handler);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return null;
};

export default StravaSyncListener;
